import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLinkedin, FaEnvelope, FaBars, FaTimes, FaPalette } from 'react-icons/fa';
import { contactInfo } from '../data';
import { useTheme, Theme } from '../contexts/ThemeContext';

const Header: React.FC = () => {
  const { theme, setTheme, themeClasses, accent } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);

  const themes: { name: Theme; label: string; swatch: string }[] = [
    { name: 'slate', label: 'Slate', swatch: 'bg-teal-400' },
    { name: 'studio', label: 'Studio', swatch: 'bg-zinc-200 border border-zinc-400' },
    { name: 'midnight', label: 'Midnight', swatch: 'bg-sky-400' }
  ];

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Experience', path: '/experience' },
    { name: 'Products', path: '/products' },
    { name: 'Channels', path: '/channels' },
    { name: 'Motocross', path: '/motocross' },
    { name: 'Photography', path: '/photography/motorsport' },
    { name: 'Projects', path: '/projects' },
    { name: 'Skills', path: '/skills' },
    { name: 'Contact', path: '/contact' }
  ];

  const closeMobile = () => setIsMobileMenuOpen(false);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 ${themeClasses.bg}/90 backdrop-blur-md border-b ${themeClasses.border}/20`}
    >
      <nav className="container mx-auto px-2 sm:px-4 md:px-6 py-4">
        <div className="flex items-center justify-between gap-2">
          <Link to="/" onClick={closeMobile}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className={`text-lg sm:text-xl font-bold bg-gradient-to-r ${themeClasses.gradient} bg-clip-text text-transparent`}
            >
              Pradap Pandiyan
            </motion.div>
          </Link>

          <div className="hidden xl:flex items-center gap-4">
            {navItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive ? accent.text : themeClasses.text
                  } ${accent.hoverText}`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`xl:hidden p-2 rounded-lg ${themeClasses.text} ${accent.softBg}`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
          </button>

          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                className={`p-2 rounded-lg border transition-colors ${accent.softBg} ${accent.softBorder} ${accent.text}`}
                title="Change Theme"
              >
                <FaPalette className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </motion.button>

              <AnimatePresence>
                {isThemeMenuOpen && (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-40"
                      onClick={() => setIsThemeMenuOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      className={`absolute right-0 mt-2 w-48 ${themeClasses.bg} rounded-xl border ${themeClasses.border}/30 shadow-xl overflow-hidden z-50`}
                    >
                      <div className="py-2">
                        {themes.map((t) => (
                          <button
                            key={t.name}
                            onClick={() => {
                              setTheme(t.name);
                              setIsThemeMenuOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 ${themeClasses.text} flex items-center gap-3 ${accent.softBg.replace('bg-', 'hover:bg-')} ${
                              theme === t.name ? accent.softBg : ''
                            }`}
                          >
                            <span className={`h-3 w-3 rounded-full ${t.swatch}`} />
                            <span>{t.label}</span>
                            {theme === t.name && <span className="ml-auto text-sm">✓</span>}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <motion.a
              whileHover={{ scale: 1.05 }}
              href={contactInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 rounded-lg border ${accent.softBg} ${accent.softBorder} ${accent.text}`}
            >
              <FaLinkedin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.05 }}
              href={`mailto:${contactInfo.email}`}
              className={`p-2 rounded-lg border ${accent.softBg} ${accent.softBorder} ${accent.text}`}
            >
              <FaEnvelope className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </motion.a>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className={`xl:hidden border-t ${themeClasses.border}/20 mt-3 pt-3 max-h-[min(70vh,calc(100dvh-5rem))] overflow-y-auto overscroll-contain`}
            >
              <div className="flex flex-col gap-1 pb-3">
                {navItems.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      `block min-h-[44px] py-2.5 px-3 rounded-lg font-medium touch-manipulation ${
                        isActive ? `${accent.text} ${accent.softBg}` : themeClasses.text
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;
